import { useState, useEffect } from "react";
import { AdminLayout } from "@/project/jain_elibrary-admin/components/layout/AdminLayout";
import { Button } from "@/project/jain_elibrary-admin/components/ui/button";
import { Input } from "@/project/jain_elibrary-admin/components/ui/input";
import { Label } from "@/project/jain_elibrary-admin/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/jain_elibrary-admin/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/jain_elibrary-admin/components/ui/card";
import { Checkbox } from "@/project/jain_elibrary-admin/components/ui/checkbox";
import {
  Download,
  Loader2,
  FileText,
  FileSpreadsheet,
  FileJson,
  Filter,
  Search,
  Eye,
  FileText as FileTextIcon,
} from "lucide-react";
import { adminService, Statistics } from "../../services/adminService";
import { getPreviewCountMatchingStatistics } from "./exportPreviewStats";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";

type ExportFormat = "csv" | "excel" | "json";
type ContentMode = "metadata" | "full";

interface AudioFilters {
  language?: string;
  search?: string;
}

interface ExportProgress {
  currentPage: number;
  totalPages: number;
  fetchedRecords: number;
  status: string;
}

// Available Audio fields for export
const AUDIO_FIELDS = [
  { key: "sr_no", label: "SR No" },
  { key: "title_english", label: "Title (English)" },
  { key: "title_language", label: "Title (Native)" },
  { key: "author", label: "Author" },
  { key: "editor", label: "Editor" },
  { key: "translator", label: "Translator" },
  { key: "publisher", label: "Publisher" },
  { key: "pub_year", label: "Publication Year" },
  { key: "pages", label: "Pages" },
  { key: "language", label: "Language" },
  { key: "classification", label: "Classification" },
  { key: "category1", label: "Category 1" },
  { key: "category2", label: "Category 2" },
  { key: "category3", label: "Category 3" },
  { key: "description", label: "Description" },
  { key: "comment", label: "Comment" },
  { key: "keyword", label: "Keyword" },
  { key: "century", label: "Century" },
  { key: "source", label: "Source" },
  { key: "downloads", label: "Downloads" },
  { key: "views", label: "Views" },
];

// Download CSV function
const downloadCSVFile = (csvData: string, filename: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      if (!csvData || csvData.length === 0) {
        reject(new Error("CSV data is empty"));
        return;
      }

      const BOM = "\uFEFF";
      const csvWithBOM = BOM + csvData;
      const blob = new Blob([csvWithBOM], {
        type: "text/csv;charset=utf-8;",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `${filename}.csv`;
      link.style.display = "none";
      document.body.appendChild(link);

      requestAnimationFrame(() => {
        try {
          link.click();
          setTimeout(() => {
            if (document.body.contains(link)) {
              document.body.removeChild(link);
            }
            URL.revokeObjectURL(url);
            resolve();
          }, 200);
        } catch (clickError) {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(url);
          reject(clickError);
        }
      });
    } catch (error) {
      reject(error);
    }
  });
};

// Download JSON function
const downloadJSONFile = (jsonData: any, filename: string): void => {
  const jsonString = JSON.stringify(jsonData, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Download Excel (CSV with .xlsx extension - simplified)
const downloadExcelFile = (
  csvData: string,
  filename: string
): Promise<void> => {
  return downloadCSVFile(csvData, filename.replace(".xlsx", ""));
};

const ExportAudio = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState<AudioFilters>({
    language: "",
    search: "",
  });
  const [exportFormat, setExportFormat] = useState<ExportFormat>("csv");
  const [contentMode] = useState<ContentMode>("metadata");
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set([
      "sr_no",
      "title_english",
      "title_language",
      "author",
      "publisher",
      "language",
      "classification",
    ])
  );
  const [exportProgress, setExportProgress] = useState<ExportProgress>({
    currentPage: 0,
    totalPages: 0,
    fetchedRecords: 0,
    status: "",
  });
  const [previewResults, setPreviewResults] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  // Options for filters
  const [authors, setAuthors] = useState<any[]>([]);
  const [publishers, setPublishers] = useState<any[]>([]);
  // Hardcoded list of 11 languages available in the system
  const languages = [
    { name: "Apabhramsha" },
    { name: "Braj" },
    { name: "Devnagari" },
    { name: "Dhundari" },
    { name: "English" },
    { name: "Germany" },
    { name: "Gujarati" },
    { name: "Hindi" },
    { name: "Marathi" },
    { name: "Prakrita" },
    { name: "Sanskrit" },
  ];
  const [_loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
    adminService
      .getStatistics()
      .then(setStatistics)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to fetch all records with pagination
  const fetchAllRecords = async (
    fetchFn: (params: any) => Promise<any>,
    pageSize: number = 100,
    recordType: string = "records"
  ): Promise<any[]> => {
    const allRecords: any[] = [];
    let page = 1;
    let hasMore = true;
    const maxPages = 100; // Safety limit
    let pagesFetched = 0;

    console.log(`Starting to fetch ${recordType}...`);

    while (hasMore && pagesFetched < maxPages) {
      try {
        console.log(
          `Fetching ${recordType} - page ${page}, page_size ${pageSize}`
        );
        const response = await fetchFn({ page, page_size: pageSize });
        console.log(`${recordType} response:`, response);

        // Handle different response formats
        let results: any[] = [];
        if (Array.isArray(response)) {
          results = response;
          hasMore = false; // If it's an array, assume it's all the data
        } else if (response && Array.isArray(response.results)) {
          results = response.results;
        } else if (response?.data && Array.isArray(response.data.results)) {
          results = response.data.results;
        } else if (response?.data && Array.isArray(response.data)) {
          results = response.data;
        }

        console.log(
          `${recordType} - extracted ${results.length} results from page ${page}`
        );

        if (results.length > 0) {
          allRecords.push(...results);
        }

        // Check if there's more data
        const totalCount = response?.count ?? 0;
        const hasNext = response?.next ?? false;

        if (hasNext && results.length === pageSize) {
          // There's a next page and we got a full page of results
          page++;
          pagesFetched++;
        } else if (totalCount > 0 && allRecords.length >= totalCount) {
          // We've fetched all records
          hasMore = false;
        } else if (results.length < pageSize) {
          // Got less than pageSize, so this is the last page
          hasMore = false;
        } else {
          // No more data
          hasMore = false;
        }
      } catch (error) {
        console.error(`Error fetching ${recordType} on page ${page}:`, error);
        hasMore = false;
      }
    }

    console.log(
      `Finished fetching ${recordType}. Total records: ${allRecords.length}`
    );
    return allRecords;
  };

  const fetchFilterOptions = async () => {
    // Immediately enable dropdowns - they can work with empty options initially
    setLoadingOptions(false);

    try {
      console.log("Starting to fetch filter options...");

      // Fetch all authors and publishers in parallel
      const [allAuthors, allPublishers] = await Promise.all([
        fetchAllRecords(
          params => adminService.getAuthors(params),
          100,
          "authors"
        ).catch(err => {
          console.error("Error fetching authors:", err);
          return [];
        }),
        fetchAllRecords(
          params => adminService.getPublishers(params),
          100,
          "publishers"
        ).catch(err => {
          console.error("Error fetching publishers:", err);
          return [];
        }),
      ]);

      console.log("Filter options fetched:", {
        authors: allAuthors.length,
        publishers: allPublishers.length,
      });

      setAuthors(Array.isArray(allAuthors) ? allAuthors : []);
      setPublishers(Array.isArray(allPublishers) ? allPublishers : []);

      console.log(
        `State updated - Authors: ${authors.length}, Publishers: ${publishers.length}`
      );
    } catch (error: any) {
      showErrorToast(error, "Failed to load filter options");
      // Ensure we set empty arrays on error
      setAuthors([]);
      setPublishers([]);
    }
  };

  // Fetch all Audios with pagination
  const fetchAllAudios = async (params: any = {}): Promise<any[]> => {
    const allAudios: any[] = [];
    let page = 1;
    const pageSize = 100;
    let hasMore = true;
    let totalCount = 0;
    let estimatedPages = 1;

    while (hasMore) {
      try {
        setExportProgress({
          currentPage: page,
          totalPages: estimatedPages,
          fetchedRecords: allAudios.length,
          status: `Fetching Audios page ${page}...`,
        });

        // Only pass supported parameters: page, page_size, search, ordering
        const AudioParams: any = {
          page,
          page_size: pageSize,
        };
        if (params.search) {
          AudioParams.search = params.search;
        }
        if (params.language) {
          AudioParams.language = params.language;
        }
        if (params.ordering) {
          AudioParams.ordering = params.ordering;
        }

        console.log(
          `ExportAudio - Fetching page ${page} with params:`,
          AudioParams
        );
        const response = await adminService.getAudio(AudioParams);
        console.log(
          `ExportAudio - Page ${page} response: count=${response.count}, results=${response.results?.length}`
        );

        if (page === 1 && response.count) {
          totalCount = response.count;
          estimatedPages = Math.ceil(totalCount / pageSize);
        }

        allAudios.push(...response.results);

        if (!response.next) {
          hasMore = false;
        } else {
          page++;
        }
      } catch (error: any) {
        console.error(`Error fetching Audios page ${page}:`, error);
        showErrorToast(error, `Failed to fetch Audios page ${page}`);
        hasMore = false;
      }
    }

    return allAudios;
  };

  // Convert Audios to CSV
  const convertToCSV = (Audios: any[]): string => {
    if (Audios.length === 0) {
      return "";
    }

    // Use selected fields or default to all available fields
    const headers = Array.from(selectedFields).filter(key =>
      AUDIO_FIELDS.some(field => field.key === key)
    );

    if (headers.length === 0) {
      // If no fields selected, use default fields
      headers.push(
        "sr_no",
        "title_english",
        "title_language",
        "author",
        "publisher",
        "language",
        "classification"
      );
    }

    // Create CSV rows
    const rows = Audios.map(Audio => {
      return headers.map(header => {
        // Handle field name variations for Audios
        // Audio API returns: title_en/title_english, publisher_name/publisher, authors array
        let value: any;

        switch (header) {
          case "title_english":
            value = Audio.title_english ?? Audio.title_en ?? "";
            break;
          case "author":
            // Handle authors array like magazines
            if (
              Audio.authors &&
              Array.isArray(Audio.authors) &&
              Audio.authors.length > 0
            ) {
              value =
                Audio.authors[0]?.name ??
                Audio.authors[0] ??
                Audio.authors.join(", ");
            } else {
              value = Audio.author ?? "";
            }
            break;
          case "publisher":
            value = Audio.publisher_name ?? Audio.publisher ?? "";
            break;
          default:
            value = Audio[header];
        }

        // For metadata mode, exclude full content fields
        if (contentMode === "metadata") {
          // Skip description/comment if they're too long (full content)
          if (
            (header === "description" || header === "comment") &&
            value &&
            value.length > 500
          ) {
            value = value.substring(0, 500) + "...";
          }
        }

        const stringValue =
          value !== null && value !== undefined ? String(value) : "";
        if (
          stringValue.includes(",") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
    });

    return [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
  };

  // Preview filtered results
  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      setShowPreview(false);
      toast.info("Loading filtered results...");

      const params: any = {
        page: 1,
        page_size: 100, // Show first 100 results in preview
      };

      // Add language filter if provided
      if (filters.language && filters.language !== "all") {
        params.language = filters.language.trim();
      }

      // Add search filter if provided
      if (filters.search?.trim()) {
        params.search = filters.search.trim();
      }

      console.log("ExportAudio - Preview params:", params);
      const response = await adminService.getAudio(params);
      console.log("ExportAudio - Preview response count:", response.count);
      console.log(
        "ExportAudio - Preview response results length:",
        response.results?.length
      );

      // Log first record to see what fields are available
      if (response.results && response.results.length > 0) {
        const firstRecord = response.results[0];
        console.log(
          "ExportAudio - First record fields:",
          Object.keys(firstRecord)
        );
        console.log("ExportAudio - First record sample:", firstRecord);

        // Specifically check for author-related fields
        const authorFields = Object.keys(firstRecord).filter(
          key =>
            key.toLowerCase().includes("author") ||
            key.toLowerCase().includes("writer") ||
            key.toLowerCase().includes("creator")
        );
        console.log("ExportAudio - Author-related fields found:", authorFields);
        if (authorFields.length > 0) {
          authorFields.forEach(field => {
            console.log(`ExportAudio - ${field}:`, firstRecord[field]);
          });
        }
      }

      const hasListFilters =
        Boolean(filters.search?.trim()) ||
        Boolean(
          filters.language &&
            filters.language !== "all" &&
            filters.language.trim()
        );

      const previewCount = getPreviewCountMatchingStatistics(
        "audio",
        statistics,
        response.count ?? 0,
        hasListFilters
      );

      setPreviewResults(response.results ?? []);
      setPreviewTotal(previewCount);
      setShowPreview(true);

      if (previewCount === 0) {
        toast.warning("No Audios found matching your filters");
      } else {
        toast.success(
          `Found ${previewCount.toLocaleString()} Audio(s). Showing first ${response.results.length} in preview.`
        );
      }
    } catch (error: any) {
      console.error("Error previewing results:", error);
      showErrorToast(error, "Failed to load preview. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: 0,
        status: "Starting export...",
      });

      toast.info("Fetching Audios data... This may take a few minutes.");

      // Build filter params
      const params: any = {};

      // Add language filter if provided
      if (filters.language && filters.language !== "all") {
        params.language = filters.language.trim();
      }

      // Add search filter if provided
      if (filters.search?.trim()) {
        params.search = filters.search.trim();
      }

      console.log("ExportAudio - Export params:", params);
      // Fetch all Audios - only Audios, nothing else
      const allAudios = await fetchAllAudios(params);
      console.log("ExportAudio - Total Audios fetched:", allAudios.length);

      if (allAudios.length === 0) {
        toast.warning("No Audios found to export");
        setExporting(false);
        return;
      }

      // Filter content based on mode
      let AudiosToExport = allAudios;
      if (contentMode === "metadata") {
        // Remove or truncate full content fields
        AudiosToExport = allAudios.map(Audio => {
          const { description, comment, ...metadata } = Audio;
          return {
            ...metadata,
            description:
              description && description.length > 500
                ? description.substring(0, 500) + "..."
                : description,
            comment:
              comment && comment.length > 500
                ? comment.substring(0, 500) + "..."
                : comment,
          };
        });
      }

      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: AudiosToExport.length,
        status: "Converting to export format...",
      });

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `Audios_export_${timestamp}_${contentMode}`;

      // Export based on format
      if (exportFormat === "json") {
        downloadJSONFile(AudiosToExport, filename);
        toast.success(
          `Successfully exported ${AudiosToExport.length} Audio(s) to JSON`
        );
      } else if (exportFormat === "csv" || exportFormat === "excel") {
        const csvData = convertToCSV(AudiosToExport);
        if (exportFormat === "excel") {
          await downloadExcelFile(csvData, filename);
        } else {
          await downloadCSVFile(csvData, filename);
        }
        toast.success(
          `Successfully exported ${AudiosToExport.length} Audio(s) to ${exportFormat.toUpperCase()}`
        );
      }

      setExportProgress({
        currentPage: 0,
        totalPages: 0,
        fetchedRecords: AudiosToExport.length,
        status: "Export completed!",
      });
    } catch (error: any) {
      console.error("Error exporting Audios:", error);
      showErrorToast(error, "Failed to export Audios");
    } finally {
      setExporting(false);
    }
  };

  const toggleField = (fieldKey: string) => {
    const newFields = new Set(selectedFields);
    if (newFields.has(fieldKey)) {
      newFields.delete(fieldKey);
    } else {
      newFields.add(fieldKey);
    }
    setSelectedFields(newFields);
  };

  const selectAllFields = () => {
    setSelectedFields(new Set(AUDIO_FIELDS.map(f => f.key)));
  };

  const deselectAllFields = () => {
    setSelectedFields(new Set());
  };

  const totalAudios = statistics?.total_audio ?? 0;

  return (
    <AdminLayout>
      <div className="min-w-0 space-y-4 md:space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground md:text-3xl">
              Export Audio Data
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Export Audios with custom filters and field selection
            </p>
          </div>
        </div>

        {/* Statistics Card */}
        {statistics && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
                <div className="flex items-center gap-3">
                  <FileTextIcon className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Total Audios
                    </p>
                    <p className="text-2xl font-bold">
                      {totalAudios.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Export Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="language">Language</Label>
                <Select
                  value={filters.language ?? "all"}
                  onValueChange={value =>
                    setFilters({
                      ...filters,
                      language: value === "all" ? undefined : value,
                    })
                  }
                >
                  <SelectTrigger id="language">
                    <SelectValue placeholder="All Languages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Languages</SelectItem>
                    {languages.map(language => (
                      <SelectItem key={language.name} value={language.name}>
                        {language.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  placeholder="Search in titles, keywords..."
                  value={filters.search ?? ""}
                  onChange={e =>
                    setFilters({
                      ...filters,
                      search: e.target.value || undefined,
                    })
                  }
                />
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button
                onClick={handlePreview}
                disabled={previewLoading}
                variant="outline"
                className="flex items-center gap-2"
              >
                {previewLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Preview Results
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Results Table */}
        {showPreview && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Filtered Results Preview
                </span>
                <span className="text-sm font-normal text-muted-foreground">
                  {previewTotal.toLocaleString()} total Audio(s) found
                  {previewResults.length < previewTotal &&
                    ` (Showing first ${previewResults.length})`}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {previewResults.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No Audios found matching your filters
                </div>
              ) : (
                <div
                  className="w-full overflow-x-auto overflow-y-auto rounded-lg border"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "60vh",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <table
                    className="table-fixed border-collapse"
                    style={{
                      minWidth: "800px",
                      width: "100%",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead className="bg-muted">
                      <tr>
                        <th className="h-12 w-24 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          SR No
                        </th>
                        <th className="h-12 min-w-[200px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Title (English)
                        </th>
                        <th className="h-12 min-w-[200px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Title (Native)
                        </th>
                        <th className="h-12 min-w-[150px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Author
                        </th>
                        <th className="h-12 min-w-[150px] border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Publisher
                        </th>
                        <th className="h-12 w-24 whitespace-nowrap border-b px-4 text-left align-middle font-medium text-muted-foreground">
                          Classification
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewResults.map((Audio, index) => (
                        <tr
                          key={Audio.sr_no ?? Audio.id ?? index}
                          className="border-b transition-colors hover:bg-muted/50"
                        >
                          <td className="whitespace-nowrap p-4 align-middle font-mono text-xs">
                            {Audio.sr_no ?? Audio.id ?? "-"}
                          </td>
                          <td className="min-w-[200px] p-4 align-middle">
                            <div
                              className="max-w-[300px] truncate"
                              title={
                                Audio.title_english ?? Audio.title_en ?? "-"
                              }
                            >
                              {Audio.title_english ?? Audio.title_en ?? "-"}
                            </div>
                          </td>
                          <td className="min-w-[200px] p-4 align-middle">
                            <div
                              className="max-w-[300px] truncate"
                              title={
                                Audio.title_language ??
                                Audio.title_native ??
                                "-"
                              }
                            >
                              {Audio.title_language ??
                                Audio.title_native ??
                                "-"}
                            </div>
                          </td>
                          <td className="min-w-[150px] p-4 align-middle">
                            <div
                              className="max-w-[200px] truncate"
                              title={
                                Audio.authors &&
                                Array.isArray(Audio.authors) &&
                                Audio.authors.length > 0
                                  ? (Audio.authors[0]?.name ??
                                    Audio.authors[0] ??
                                    Audio.authors.join(", "))
                                  : (Audio.author ?? "-")
                              }
                            >
                              {Audio.authors &&
                              Array.isArray(Audio.authors) &&
                              Audio.authors.length > 0
                                ? (Audio.authors[0]?.name ??
                                  Audio.authors[0] ??
                                  Audio.authors.join(", "))
                                : (Audio.author ?? "-")}
                            </div>
                          </td>
                          <td className="min-w-[150px] p-4 align-middle">
                            <div
                              className="max-w-[200px] truncate"
                              title={
                                Audio.publisher_name ?? Audio.publisher ?? "-"
                              }
                            >
                              {Audio.publisher_name ?? Audio.publisher ?? "-"}
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-4 align-middle">
                            {Audio.classification ??
                              Audio.category1 ??
                              Audio.category2 ??
                              Audio.category3 ??
                              "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Custom Field Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Select Fields to Export</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose which fields to include in the exported file
            </p>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllFields}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAllFields}>
                Deselect All
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {AUDIO_FIELDS.map(field => (
                <div key={field.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={field.key}
                    checked={selectedFields.has(field.key)}
                    onCheckedChange={() => toggleField(field.key)}
                  />
                  <Label
                    htmlFor={field.key}
                    className="cursor-pointer text-sm font-normal"
                  >
                    {field.label}
                  </Label>
                </div>
              ))}
            </div>
            {selectedFields.size === 0 && (
              <p className="mt-4 text-sm text-muted-foreground">
                ⚠️ No fields selected. Default fields will be used.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Export Format Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Export Format</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 overflow-x-hidden">
            <div className="grid w-full min-w-0 grid-cols-1 gap-3 xl:grid-cols-[repeat(3,minmax(0,1fr))]">
              <Button
                variant={exportFormat === "csv" ? "default" : "outline"}
                onClick={() => setExportFormat("csv")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileText className="h-4 w-4 shrink-0" />
                CSV
              </Button>
              <Button
                variant={exportFormat === "excel" ? "default" : "outline"}
                onClick={() => setExportFormat("excel")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileSpreadsheet className="h-4 w-4 shrink-0" />
                Excel
              </Button>
              <Button
                variant={exportFormat === "json" ? "default" : "outline"}
                onClick={() => setExportFormat("json")}
                className="flex min-h-10 w-full min-w-0 max-w-full items-center justify-center gap-2 whitespace-normal"
              >
                <FileJson className="h-4 w-4 shrink-0" />
                JSON
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export Progress */}
        {exporting && (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium">{exportProgress.status}</span>
                </div>
                {exportProgress.totalPages > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progress:</span>
                    <span className="font-medium">
                      Page {exportProgress.currentPage} of{" "}
                      {exportProgress.totalPages}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Records Fetched:
                  </span>
                  <span className="font-medium">
                    {exportProgress.fetchedRecords.toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleExport}
            disabled={exporting || loading}
            size="lg"
            className="flex items-center gap-2"
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export Audios
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ExportAudio;
